import { Fee } from "app/shared/model/Fee";
import { getLocalApi } from "./localApi";
import { getSelectedDivisionId } from "app/shared/division/selectedDivisionStorage";
import apiConfig from "app/api/apiConfig.json";
import { ApiErrors } from "./ApiErrors";

export interface SaveFeeResult {
    ok: boolean;
    message?: string;
    errors?: string[];
    data?: Fee;
    successful?: boolean;
}

/**
 * Saves a Fee product via the API.
 * @param fee - The Fee object to save
 */
export async function saveFee(fee: Fee): Promise<SaveFeeResult> {
    const localApi = await getLocalApi();

    const divisionId = getSelectedDivisionId();
    const endpoint = apiConfig.fees_save.replace("{divisionId}", divisionId.toString());
    return await localApi.call(endpoint, "PUT", JSON.stringify(fee))
        .then(handleSaveResult);
}

async function handleSaveResult(response: Response): Promise<SaveFeeResult> {
    const fallbackError = 'Failed to save fee';

    let rawText = '';
    try {
        rawText = await response.text();
    } catch {
        rawText = '';
    }

    const safeParse = () => {
        if (!rawText) {
            return undefined;
        }
        try {
            return JSON.parse(rawText);
        } catch {
            return undefined;
        }
    };

    const parsed = safeParse();
    const errorMessages = ApiErrors.extractAllErrors(parsed, response, fallbackError);

    if (response.ok) {
        if (errorMessages.length > 0) {
            const message = ApiErrors.formatErrors(errorMessages);
            return {
                ok: false,
                successful: parsed?.successful,
                errors: errorMessages,
                message,
            };
        }
        
        if (parsed?.successful === false) {
            const message = ApiErrors.formatErrors(errorMessages) || fallbackError;
            return {
                ok: false,
                successful: parsed.successful,
                errors: errorMessages.length > 0 ? errorMessages : undefined,
                message,
            };
        }

        if (!rawText) {
            return { ok: true };
        }

        const data: Fee | undefined = parsed?.data ?? parsed;
        return {
            ok: true,
            successful: parsed?.successful,
            data,
        };
    }
    
    const message = ApiErrors.formatErrors(errorMessages);

    if (response.status === 400 && parsed) {
        return {
            ok: false,
            successful: parsed.successful,
            errors: errorMessages.length > 0 ? errorMessages : undefined,
            message,
        };
    }

    return {
        ok: false,
        message,
        errors: errorMessages.length > 0 ? errorMessages : undefined,
    };
}
